import { TrajectoriesChart } from "charts/TrajectoriesChart";
import { useReadScrolly } from "components/scrolly";
import { format } from "d3-format";
import { goalRule, xAxisTitleLookup, yAxisTitleLookup } from "domain/trajectories";
import { useCallback, useMemo } from "react";
import { defineMessage, useIntl } from "react-intl";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_LMIC";
import { datasets } from "../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_LMIC_datasets";
import { RiseDataDecoder } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { E, io, O, pipe } from "../prelude";
export * from "../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_LMIC";
// Scrollytelling steps
const Steps = io.union([
    io.literal("FADED"),
    io.literal("CURRENT"),
    io.literal("SIMULATION_ACCESS"),
    io.literal("SIMULATION_LEARNING"),
    io.literal("COMPARISON"),
    io.literal("default")
]);
// Translation lookup
const seriesLabelsLookup = {
    CURRENT: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.label.current",
        defaultMessage: "Low-performing countries average"
    }),
    SIMULATION_ACCESS: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.label.simulationAccess",
        defaultMessage: "Access simulation"
    }),
    SIMULATION_LEARNING: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.label.simulationLearning",
        defaultMessage: "Learning simulation"
    }),
    COMPARISON: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.label.comparison",
        defaultMessage: "High-performing countries average"
    })
};
const legendLookup = {
    CURRENT: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.legend.current",
        defaultMessage: "Current average of five higher-performing countries"
    }),
    SIMULATION_ACCESS: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.legend.simulationAccess",
        defaultMessage: "Access simulation  (100% grade attainment * learning per grade of in-school children in low-performing countries)"
    }),
    SIMULATION_LEARNING: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.legend.simulationLearning",
        defaultMessage: "Learning simulation (grade attainment in low-performing countries * learning per grade in higher-performing countries)"
    }),
    COMPARISON: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_LMIC.legend.comparison",
        defaultMessage: "Current average of five low-performing countries"
    })
};
export const colors = {
    current: M.q07Palette[6],
    accessSimulation: M.q08Palette[5],
    learningSimulation: M.q01Palette[3],
    comparison: M.q03Palette[7]
};
export const makeLineStyler = (highlightedSeries)=>(series)=>{
        const strokeWidth = 3;
        const strokeOpacity = highlightedSeries ? highlightedSeries === series ? 1 : 0.1 : 1;
        if (series === "CURRENT") {
            return {
                strokeOpacity,
                strokeWidth,
                stroke: colors.current
            };
        }
        if (series === "SIMULATION_ACCESS") {
            return {
                strokeOpacity,
                strokeWidth,
                stroke: colors.accessSimulation,
                strokeDasharray: "6 12"
            };
        }
        if (series === "SIMULATION_LEARNING") {
            return {
                strokeOpacity,
                strokeWidth,
                stroke: colors.learningSimulation,
                strokeDasharray: "6 12"
            };
        }
        if (series === "COMPARISON") {
            return {
                strokeOpacity,
                strokeWidth,
                stroke: colors.comparison
            };
        }
        return {
            strokeWidth,
            stroke: M.grayscalePalette[4]
        };
    };
export const Data = RiseDataDecoder([
    "value",
    "country",
    "year",
    "series"
], {});
export const Chart = ({ data, renderMode })=>{
    const screenSDown = !useMedia(breakpoints.s);
    const { activeSection } = useReadScrolly();
    const intl = useIntl();
    const activeSectionId = pipe(Steps.decode(O.toNullable(activeSection)), E.getOrElse(()=>Steps.encode("default")));
    const lineStyler = useMemo(()=>makeLineStyler(undefined), []);
    const chartPropsAtScrollSteps = {
        FADED: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: 0.5
                }),
            isInteractive: false
        },
        CURRENT: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: series === "CURRENT" ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>series !== "CURRENT"
        },
        SIMULATION_ACCESS: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: [
                        "CURRENT",
                        "SIMULATION_ACCESS"
                    ].includes(series.toString()) ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>![
                    "CURRENT",
                    "SIMULATION_ACCESS"
                ].includes(series.toString())
        },
        SIMULATION_LEARNING: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: [
                        "CURRENT",
                        "SIMULATION_LEARNING"
                    ].includes(series.toString()) ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>![
                    "CURRENT",
                    "SIMULATION_LEARNING"
                ].includes(series.toString())
        },
        COMPARISON: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: [
                        "CURRENT",
                        "COMPARISON"
                    ].includes(series.toString()) ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>![
                    "CURRENT",
                    "COMPARISON"
                ].includes(series.toString())
        },
        default: {
            lineStyle: lineStyler
        }
    };
    const seriesAccessor = useCallback((d)=>d.series, []);
    const xAccessor = useCallback((d)=>d.year, []);
    const yAccessor = useCallback((d)=>d.value, []);
    return <TrajectoriesChart height={M.chartHeight.m} dataset={data} seriesAccessor={seriesAccessor} xAccessor={xAccessor} yAccessor={yAccessor} yAxisTitle={intl.formatMessage(yAxisTitleLookup.LIT)} xAxisTitle={intl.formatMessage(xAxisTitleLookup.AGE)} formatTooltipLabel={(d)=>intl.formatMessage(seriesLabelsLookup[d.series]) || ""} formatTooltipValue={(d)=>d.value === null ? intl.formatMessage(defineMessage({
            id: "fig.label.data.not.available",
            defaultMessage: "Not Available"
        })) : format(".0%")(d.value)} formatLegendLabel={(series)=>intl.formatMessage(legendLookup[series]) || ""} rules={renderMode === "embed" ? [] : [
        goalRule(intl, "AGE", "LIT")
    ]} showColorLegend={true} legendValues={[
        "COMPARISON",
        "SIMULATION_LEARNING",
        "SIMULATION_ACCESS",
        "CURRENT"
    ]} flipTooltip={screenSDown} {...chartPropsAtScrollSteps[activeSectionId]}/>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_LMIC.json", import.meta.url).href,
    csv: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_LMIC.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_LMIC.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "main",
    datasets
});
